import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { trpcClient, useTRPC } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { getPublicUserLabels } from '@/social/public-user-labels'
import {
  useInvalidateSocial,
  useSocialWorkspace,
} from '@/social/social-provider'
import type {
  PublicSocialUser,
  SocialOverview,
  SocialSearchUser,
} from '@/social/types'
import { useSocialText } from '@/social/use-social-text'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import {
  Ban,
  Check,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Search,
  ShieldCheck,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { UsernameRequiredPrompt } from './username-required-prompt'

type RelationshipAction
  = | { action: 'request', targetUserId: string }
    | { action: 'accept' | 'decline' | 'cancel', connectionId: string }
    | { action: 'remove' | 'block' | 'unblock', targetUserId: string }

function UserIdentity({ user }: { user: PublicSocialUser }) {
  const labels = getPublicUserLabels(user)
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{labels.primary}</p>
      {labels.secondary && (
        <p className="truncate text-xs text-muted-foreground">
          {labels.secondary}
        </p>
      )}
    </div>
  )
}

function SectionTitle({
  children,
  count,
}: {
  children: string
  count?: number
}) {
  return (
    <div className="mb-2 flex items-center justify-between px-1">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {children}
      </h3>
      {!!count && (
        <Badge variant="secondary" className="h-5 min-w-5 px-1 text-[10px]">
          {count}
        </Badge>
      )}
    </div>
  )
}

export function SocialPeoplePanel({
  overview,
  activeConnectionId,
  onSelectConnection,
  onOpenProfile,
  className,
}: {
  overview: SocialOverview
  activeConnectionId: string | null
  onSelectConnection: (connectionId: string) => void
  onOpenProfile: () => void
  className?: string
}) {
  const trpc = useTRPC()
  const text = useSocialText()
  const invalidateSocial = useInvalidateSocial()
  const { publishDataEvent } = useSocialWorkspace()
  const panelRef = useRef<HTMLElement>(null)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [pendingAction, setPendingAction] = useState('')
  const canUseFriends
    = !!overview.profile.username || overview.profile.isAdministrator
  const administratorPages = useInfiniteQuery({
    queryKey: ['socialRouter', 'listAdministratorConversations'],
    queryFn: ({ pageParam }) =>
      trpcClient.socialRouter.listAdministratorConversations.query(
        pageParam ? { cursor: pageParam } : {},
      ),
    initialPageParam: '',
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    enabled: overview.profile.isAdministrator,
  })
  const administratorConversations = useMemo(() => {
    const byConnection = new Map(
      overview.administratorConversations.map(conversation => [
        conversation.connectionId,
        conversation,
      ]),
    )
    for (const page of administratorPages.data?.pages ?? []) {
      for (const conversation of page.conversations) {
        if (!byConnection.has(conversation.connectionId))
          byConnection.set(conversation.connectionId, conversation)
      }
    }
    return [...byConnection.values()]
  }, [overview.administratorConversations, administratorPages.data])

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedQuery(query.trim().toLowerCase()),
      250,
    )
    return () => window.clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (debouncedQuery.length >= 3 && panelRef.current)
      panelRef.current.scrollTop = 0
  }, [debouncedQuery])

  const searchQuery = useQuery({
    ...trpc.socialRouter.searchUsers.queryOptions({
      query: debouncedQuery,
      limit: 20,
    }),
    enabled: canUseFriends && debouncedQuery.length >= 3,
  })
  const searchUsers = useMemo(() => {
    const data = searchQuery.data as { users?: SocialSearchUser[] } | undefined
    return data?.users ?? []
  }, [searchQuery.data])
  const relationshipMutation = useMutation(
    trpc.socialRouter.changeRelationship.mutationOptions(),
  )

  const runAction = async (action: RelationshipAction, actionKey: string) => {
    setPendingAction(actionKey)
    try {
      await relationshipMutation.mutateAsync(action)
      publishDataEvent('relationship.changed')
      await invalidateSocial()
      toast.success(
        text('social.toast.relationshipUpdated', 'Friend list updated.'),
      )
    }
    catch {
      toast.error(
        text('social.toast.actionFailed', 'Could not complete that action.'),
      )
    }
    finally {
      setPendingAction('')
    }
  }

  const actionButton = (
    action: RelationshipAction,
    key: string,
    label: string,
    icon: React.ReactNode,
    variant: 'default' | 'outline' | 'ghost' = 'outline',
  ) => (
    <Button
      type="button"
      size="sm"
      variant={variant}
      disabled={!!pendingAction}
      onClick={() => runAction(action, key)}
    >
      {pendingAction === key ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        icon
      )}
      {label}
    </Button>
  )

  return (
    <aside
      ref={panelRef}
      className={cn('min-h-0 min-w-0 overflow-y-auto bg-background', className)}
    >
      {canUseFriends && (
        <div className="sticky top-0 z-10 border-b bg-background p-3">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              maxLength={overview.profile.isAdministrator ? 254 : 24}
              placeholder={
                overview.profile.isAdministrator
                  ? text(
                      'social.search.administratorPlaceholder',
                      'Search by username or email',
                    )
                  : text('social.search.placeholder', 'Search exact username')
              }
              className="pl-9"
            />
          </label>
          {query && query.trim().length < 3 && (
            <p className="mt-2 px-1 text-xs text-muted-foreground">
              {text('social.search.hint', 'Enter at least 3 characters.')}
            </p>
          )}
        </div>
      )}

      <div className="space-y-6 p-3">
        {canUseFriends && debouncedQuery.length >= 3 && (
          <section>
            <SectionTitle>
              {text('social.search.title', 'Search results')}
            </SectionTitle>
            {searchQuery.isPending ? (
              <Loader2 className="mx-auto my-5 size-5 animate-spin text-muted-foreground" />
            ) : searchQuery.isError ? (
              <p className="px-1 text-sm text-destructive">
                {text('social.search.error', 'Search failed.')}
              </p>
            ) : searchUsers.length === 0 ? (
              <p className="px-1 text-sm text-muted-foreground">
                {text('social.search.empty', 'No matching username.')}
              </p>
            ) : (
              <div className="space-y-2">
                {searchUsers.map((user) => {
                  const key = `search-${user.id}`
                  return (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 rounded-lg border p-2.5"
                    >
                      <UserIdentity user={user} />
                      {!overview.profile.isAdministrator
                        && user.relationship === 'none'
                        && actionButton(
                          { action: 'request', targetUserId: user.id },
                          key,
                          text('social.actions.add', 'Add'),
                          <UserPlus className="size-3.5" />,
                        )}
                      {!overview.profile.isAdministrator
                        && user.relationship === 'incoming'
                        && user.connectionId && (
                        <div className="flex gap-1">
                          {actionButton(
                            {
                              action: 'accept',
                              connectionId: user.connectionId,
                            },
                            key,
                            text('social.actions.accept', 'Accept'),
                            <Check className="size-3.5" />,
                          )}
                          {actionButton(
                            { action: 'block', targetUserId: user.id },
                              `block-${key}`,
                              text('social.actions.block', 'Block'),
                              <Ban className="size-3.5" />,
                              'ghost',
                          )}
                        </div>
                      )}
                      {!overview.profile.isAdministrator
                        && user.relationship === 'outgoing'
                        && user.connectionId
                        && actionButton(
                          { action: 'cancel', connectionId: user.connectionId },
                          key,
                          text('social.actions.cancel', 'Cancel'),
                          <X className="size-3.5" />,
                          'ghost',
                        )}
                      {!overview.profile.isAdministrator
                        && user.relationship === 'blockedByMe'
                        && actionButton(
                          { action: 'unblock', targetUserId: user.id },
                          `unblock-${key}`,
                          text('social.actions.unblock', 'Unblock'),
                          <Ban className="size-3.5" />,
                          'ghost',
                        )}
                      {user.relationship === 'friend'
                        && (user.connectionId ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (user.connectionId)
                                onSelectConnection(user.connectionId)
                            }}
                          >
                            <MessageCircle className="size-3.5" />
                            {text('social.friends.openChat', 'Chat')}
                          </Button>
                        ) : (
                          <Badge variant="secondary">
                            {text('social.friends.connected', 'Friend')}
                          </Badge>
                        ))}
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        )}

        {(overview.administrator
          || administratorConversations.length > 0
          || overview.profile.isAdministrator) && (
          <section>
            <SectionTitle>
              {overview.profile.isAdministrator
                ? text(
                    'social.administrator.userConversationsTitle',
                    'User conversations',
                  )
                : text('social.administrator.title', 'Administrator')}
            </SectionTitle>
            <div className="space-y-1">
              {overview.administrator && (
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg border p-2.5 text-left transition-colors hover:bg-muted/60',
                    activeConnectionId
                    === overview.administrator.connectionId
                    && 'border-primary/20 bg-primary/5',
                  )}
                  onClick={() =>
                    onSelectConnection(overview.administrator!.connectionId)}
                >
                  <span className="relative flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <ShieldCheck className="size-4" />
                    {overview.administrator.unreadCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] text-destructive-foreground">
                        {overview.administrator.unreadCount > 99
                          ? '99+'
                          : overview.administrator.unreadCount}
                      </span>
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      @{overview.administrator.user.username}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {overview.administrator.lastMessage?.type
                        === 'room_invite'
                        ? text(
                            'social.conversation.roomInvitePreview',
                            'Multiplayer room invitation',
                          )
                        : overview.administrator.lastMessage?.preview
                          || text(
                            'social.administrator.officialContact',
                            'Official contact',
                          )}
                    </p>
                  </div>
                  <MessageCircle className="size-4 shrink-0 text-muted-foreground" />
                </button>
              )}
              {administratorConversations.map((conversation) => {
                const labels = getPublicUserLabels(conversation.user)
                return (
                  <button
                    key={conversation.connectionId}
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg border p-2.5 text-left transition-colors hover:bg-muted/60',
                      activeConnectionId === conversation.connectionId
                      && 'border-primary/20 bg-primary/5',
                    )}
                    onClick={() =>
                      onSelectConnection(conversation.connectionId)}
                  >
                    <span className="relative flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      {overview.profile.isAdministrator ? (
                        <MessageCircle className="size-4" />
                      ) : (
                        <ShieldCheck className="size-4" />
                      )}
                      {conversation.unreadCount > 0 && (
                        <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] text-destructive-foreground">
                          {conversation.unreadCount > 99
                            ? '99+'
                            : conversation.unreadCount}
                        </span>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {labels.primary}
                      </p>
                      {conversation.lastMessage?.preview && (
                        <p className="truncate text-xs text-muted-foreground">
                          {conversation.lastMessage.preview}
                        </p>
                      )}
                    </div>
                    <MessageCircle className="size-4 shrink-0 text-muted-foreground" />
                  </button>
                )
              })}
              {overview.profile.isAdministrator && administratorPages.hasNextPage && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={administratorPages.isFetchingNextPage}
                  onClick={() => administratorPages.fetchNextPage()}
                >
                  {administratorPages.isFetchingNextPage && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  {text('social.administrator.loadMore', 'Load more conversations')}
                </Button>
              )}
              {overview.profile.isAdministrator && administratorPages.isError && (
                <div className="flex items-center gap-2 px-1">
                  <p className="text-sm text-destructive">
                    {text('social.administrator.loadError', 'Could not load conversations.')}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => administratorPages.refetch()}
                  >
                    {text('social.retry', 'Try again')}
                  </Button>
                </div>
              )}
            </div>
          </section>
        )}

        {!canUseFriends && overview.profile.usernameClaimEligible && (
          <UsernameRequiredPrompt compact onOpenProfile={onOpenProfile} />
        )}

        {!canUseFriends && !overview.profile.usernameClaimEligible && (
          <div className="rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground">
            {text(
              'social.username.unavailable',
              'Username setup is unavailable for this account. Please contact support.',
            )}
          </div>
        )}

        {canUseFriends
          && !overview.profile.isAdministrator
          && overview.incomingRequests.length > 0 && (
          <section>
            <SectionTitle count={overview.incomingRequests.length}>
              {text('social.requests.incoming', 'Friend requests')}
            </SectionTitle>
            <div className="space-y-2">
              {overview.incomingRequests.map(request => (
                <div
                  key={request.connectionId}
                  className="rounded-lg border p-2.5"
                >
                  <UserIdentity user={request.user} />
                  <div className="mt-2 flex gap-2">
                    {actionButton(
                      {
                        action: 'accept',
                        connectionId: request.connectionId,
                      },
                        `accept-${request.connectionId}`,
                        text('social.actions.accept', 'Accept'),
                        <Check className="size-3.5" />,
                        'default',
                    )}
                    {actionButton(
                      {
                        action: 'decline',
                        connectionId: request.connectionId,
                      },
                        `decline-${request.connectionId}`,
                        text('social.actions.decline', 'Decline'),
                        <X className="size-3.5" />,
                        'ghost',
                    )}
                    {actionButton(
                      { action: 'block', targetUserId: request.user.id },
                        `block-${request.connectionId}`,
                        text('social.actions.block', 'Block'),
                        <Ban className="size-3.5" />,
                        'ghost',
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {canUseFriends && !overview.profile.isAdministrator && (
          <section>
            <SectionTitle count={overview.friends.length}>
              {text('social.friends.title', 'Friends')}
            </SectionTitle>
            {overview.friends.length === 0 ? (
              <div className="rounded-lg border border-dashed px-4 py-6 text-center">
                <UserPlus className="mx-auto mb-2 size-5 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {text(
                    'social.friends.empty',
                    'Search for a username to add your first friend.',
                  )}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {overview.friends.map((connection) => {
                  const labels = getPublicUserLabels(connection.user)
                  const preview
                    = connection.lastMessage?.type === 'room_invite'
                      ? text(
                          'social.conversation.roomInvitePreview',
                          'Multiplayer room invitation',
                        )
                      : connection.lastMessage?.preview || labels.secondary
                  return (
                    <div
                      key={connection.connectionId}
                      className={cn(
                        'group flex items-center gap-1 rounded-lg border border-transparent p-1 transition-colors hover:bg-muted/60',
                        activeConnectionId === connection.connectionId
                        && 'border-primary/20 bg-primary/5',
                      )}
                    >
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-1.5 text-left"
                        onClick={() =>
                          onSelectConnection(connection.connectionId)}
                      >
                        <span className="relative flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                          {labels.avatar.slice(0, 2).toUpperCase()}
                          {connection.unreadCount > 0 && (
                            <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] text-destructive-foreground">
                              {connection.unreadCount > 99
                                ? '99+'
                                : connection.unreadCount}
                            </span>
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {labels.primary}
                          </p>
                          {preview && (
                            <p className="truncate text-xs text-muted-foreground">
                              {preview}
                            </p>
                          )}
                        </div>
                        <MessageCircle className="size-4 shrink-0 text-muted-foreground" />
                      </button>
                      {!overview.profile.isAdministrator && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-8 shrink-0"
                            >
                              <MoreHorizontal className="size-4" />
                              <span className="sr-only">
                                {text('social.actions.menu', 'Actions')}
                              </span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                runAction(
                                  {
                                    action: 'remove',
                                    targetUserId: connection.user.id,
                                  },
                                  `remove-${connection.connectionId}`,
                                )}
                            >
                              <UserMinus className="mr-2 size-4" />
                              {text('social.actions.remove', 'Remove friend')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() =>
                                runAction(
                                  {
                                    action: 'block',
                                    targetUserId: connection.user.id,
                                  },
                                  `block-${connection.connectionId}`,
                                )}
                            >
                              <Ban className="mr-2 size-4" />
                              {text('social.actions.block', 'Block')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        )}

        {canUseFriends
          && !overview.profile.isAdministrator
          && overview.outgoingRequests.length > 0 && (
          <section>
            <SectionTitle>
              {text('social.requests.outgoing', 'Sent requests')}
            </SectionTitle>
            <div className="space-y-2">
              {overview.outgoingRequests.map(request => (
                <div
                  key={request.connectionId}
                  className="flex items-center gap-2 rounded-lg border p-2.5"
                >
                  <UserIdentity user={request.user} />
                  {actionButton(
                    { action: 'cancel', connectionId: request.connectionId },
                      `cancel-${request.connectionId}`,
                      text('social.actions.cancel', 'Cancel'),
                      <X className="size-3.5" />,
                      'ghost',
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {canUseFriends
          && !overview.profile.isAdministrator
          && overview.blocked.length > 0 && (
          <section>
            <SectionTitle>
              {text('social.blocked.title', 'Blocked')}
            </SectionTitle>
            <div className="space-y-2">
              {overview.blocked.map(blocked => (
                <div
                  key={blocked.user.id}
                  className="flex items-center gap-2 rounded-lg border p-2.5"
                >
                  <UserIdentity user={blocked.user} />
                  {actionButton(
                    { action: 'unblock', targetUserId: blocked.user.id },
                      `unblock-${blocked.user.id}`,
                      text('social.actions.unblock', 'Unblock'),
                      <Ban className="size-3.5" />,
                      'ghost',
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </aside>
  )
}
